import AV from '../config/leancloud';
import { authService, ROLE_LEVELS } from '../services/authService';
import dayjs from 'dayjs';
import { activityLogService } from './activityLogService';



// Define field access levels
export const FIELD_ACCESS_LEVELS = {
  // Finance only fields (Level 3 and above)
  FINANCE_FIELDS: {
    verified: ROLE_LEVELS.FINANCE,  // 审核状态
    name: ROLE_LEVELS.FINANCE,      // 姓名
    uniqueId: ROLE_LEVELS.FINANCE,  // 身份证号
  },
  
  // Manager and above fields (Level 2 and above)
  PAYMENT_FIELDS: {
    paymentMethod: ROLE_LEVELS.MANAGER,  // 付款方式
    tuitionFee: ROLE_LEVELS.MANAGER,     // 学费
    receiptNumber: ROLE_LEVELS.MANAGER,  // 收据号
    paid: ROLE_LEVELS.MANAGER,           // 是否付款
  }
};

const STATUS = {
  NORMAL: '正常',
  GRADUATED: '已毕业',
  WITHDRAWN: '退学'
};

// Add these constants at the top of the file
export const STUDY_REQUIREMENTS = {
  '科目一': { minutes: 540 },
  '科目二': { minutes: 720, kilometers: 0 },
  '科目三': { minutes: 1080, kilometers: 260 },
  '科目四': { minutes: 450 }
};

export const studentService = {
  // Helper method to check uniqueness
  async checkUniqueness(field, value, excludeId = null) {
    try {
      const query = new AV.Query('Student');
      query.equalTo(field, value);
      
      if (excludeId) {
        // If updating, create a separate query to exclude current student
        const excludeQuery = new AV.Query('Student');
        excludeQuery.notEqualTo('objectId', excludeId);
        
        // Combine the queries using AV.Query.and()
        const combinedQuery = AV.Query.and(query, excludeQuery);
        return await combinedQuery.count() === 0;
      }
      
      return await query.count() === 0;
    } catch (error) {
      console.error('Error checking uniqueness:', error);
      throw error;
    }
  },

  // Improved file upload helper for multiple files
  async uploadFiles(files) {
    if (!files || !Array.isArray(files) || files.length === 0) {
      console.error('Invalid files array:', files);
      return [];
    }
    
    try {
      console.log('Starting multiple file upload:', {
        count: files.length,
        sizes: files.map(f => f.size),
        types: files.map(f => f.type)
      });

      const uploadPromises = files.map(file => {
        const avFile = new AV.File(file.name || 'unnamed_file', file);
        return avFile.save();
      });

      const savedFiles = await Promise.all(uploadPromises);
      console.log('Files uploaded successfully:', savedFiles.map(f => f.url()));
      return savedFiles;
    } catch (error) {
      console.error('Error uploading files:', error);
      throw error;
    }
  },

  // Original method kept for backward compatibility
  async uploadFile(file) {
    if (!file || !(file instanceof File)) {
      console.error('Invalid file object:', file);
      return null;
    }
    
    try {
      console.log('Starting file upload:', {
        name: file.name,
        size: file.size,
        type: file.type
      });

      const avFile = new AV.File(file.name || 'unnamed_file', file);
      const savedFile = await avFile.save();
      console.log('File uploaded successfully:', savedFile.url());
      return savedFile;
    } catch (error) {
      console.error('Error uploading file:', error);
      throw error;
    }
  },

  // Helper function to create note
  createActionNote(action, details = '') {
    const currentUser = authService.getCurrentUser();
    const username = currentUser ? currentUser.get('username') : '未知用户';
    const timestamp = new Date();

    return {
      content: `${username} ${action}${details ? `: ${details}` : ''}`,
      date: timestamp.toISOString(),
      createdAt: timestamp
    };
  },

  async createStudent(studentData) {
    console.log('Creating student with raw data:', studentData);

    try {
      // Check uniqueness of uniqueId first
      if (studentData.uniqueId) {
        const isUniqueId = await this.checkUniqueness('uniqueId', studentData.uniqueId);
        if (!isUniqueId) {
          throw new Error('该身份证号已存在');
        }
      }

      const Student = AV.Object.extend('Student');
      const student = new Student();

      // Initialize study progress fields
      const studyProgress = {
        subject1: { minutes: 0 },
        subject2: { minutes: 0, kilometers: 0 },
        subject3: { minutes: 0, kilometers: 0 },
        subject4: { minutes: 0 },
        // Add completion flags for faster querying
        subject1Completed: false,
        subject2Completed: false,
        subject3Completed: false,
        subject4Completed: false
      };

      // Process exam data for transferred student
      const exams = [];
      // Initialize individual exam fields
      const examFields = {
        exam1: null,
        exam2: null,
        exam3: null,
        exam4: null
      };

      if (studentData.transferred) {
        ['exam1', 'exam2', 'exam3', 'exam4'].forEach(examKey => {
          const examType = examKey.replace('exam', '');
          if (studentData[`${examKey}Passed`] && studentData[`${examKey}Date`]) {
            const examRecord = {
              passed: true,
              date: dayjs(studentData[`${examKey}Date`]).format('YYYY-MM-DD'),
            };

            // Add to exams array (existing functionality)
            exams.push({
              examType,
              date: dayjs(studentData[`${examKey}Date`]).format('YYYY-MM-DD'),
              passed: true,
              examiner: '转入',
              createdAt: new Date().toISOString()
            });

            // Add to individual exam fields
            examFields[examKey] = examRecord;
          }
        });
      }

      // System default values
      const systemDefaults = {
        verified: false,
        documented: studentData.transferred === true,
        status: STATUS.NORMAL,
        exams: exams  // existing functionality
      };

      // Combine with existing data
      const formattedData = {
        ...studentData,
        ...systemDefaults,
        ...studyProgress,
        ...examFields,  // Add new exam fields
        signUpDate: studentData.signUpDate ? 
          dayjs(studentData.signUpDate).format('YYYY-MM-DD') : 
          undefined
      };

      // Remove exam related fields from main student data
      ['exam1Passed', 'exam2Passed', 'exam3Passed', 'exam4Passed',
       'exam1Date', 'exam2Date', 'exam3Date', 'exam4Date'].forEach(key => {
        delete formattedData[key];
      });

      // Set other fields
      Object.entries(formattedData).forEach(([key, value]) => {
        if (
          value !== undefined && 
          value !== null && 
          key !== 'receiptPic' && 
          key !== 'tuitionReceipt' && 
          key !== 'receiptPics' &&
          key !== 'tuitionReceipts' &&
          !key.startsWith('receiptPic') &&
          !key.startsWith('tuitionReceipt') &&
          key !== 'notes'
        ) {
          student.set(key, value);
        }
      });

      // Handle file uploads first and wait for them to complete
      console.log('Checking files:', {
        receiptPic: studentData.receiptPic,
        tuitionReceipt: studentData.tuitionReceipt
      });

      // Upload receipt pics if they exist
      if (Array.isArray(studentData.receiptPic) && studentData.receiptPic.length > 0) {
        try {
          console.log('Uploading receipt pics...');
          const fileObjects = studentData.receiptPic
            .filter(file => file instanceof File || file.originFileObj)
            .map(file => file instanceof File ? file : file.originFileObj);
          
          // Upload each file individually and store in numbered fields
          for (let i = 0; i < fileObjects.length; i++) {
            if (fileObjects[i]) {
              const receiptFile = await this.uploadFile(fileObjects[i]);
              if (receiptFile) {
                // Store as separate fields: receiptPic1, receiptPic2, etc.
                student.set(`receiptPic${i+1}`, receiptFile);
                console.log(`Receipt pic ${i+1} uploaded successfully:`, receiptFile.url());
              }
            }
          }
          
          // Store the count of receipt pics for easy retrieval
          student.set('receiptPicCount', fileObjects.length);
        } catch (error) {
          console.error('Failed to upload receipt pics:', error);
          throw error;
        }
      } else if (studentData.receiptPic instanceof File) {
        // Handle single file for backward compatibility
        try {
          console.log('Uploading single receipt pic...');
          const receiptFile = await this.uploadFile(studentData.receiptPic);
          if (receiptFile) {
            student.set('receiptPic', receiptFile); // Keep for backward compatibility
            student.set('receiptPic1', receiptFile); // Also store as numbered field
            student.set('receiptPicCount', 1);
            console.log('Receipt pic uploaded successfully:', receiptFile.url());
          }
        } catch (error) {
          console.error('Failed to upload receipt pic:', error);
          throw error;
        }
      }

      // Upload tuition receipts if they exist
      if (Array.isArray(studentData.tuitionReceipt) && studentData.tuitionReceipt.length > 0) {
        try {
          console.log('Uploading tuition receipts...');
          const fileObjects = studentData.tuitionReceipt
            .filter(file => file instanceof File || file.originFileObj)
            .map(file => file instanceof File ? file : file.originFileObj);
          
          // Upload each file individually and store in numbered fields
          for (let i = 0; i < fileObjects.length; i++) {
            if (fileObjects[i]) {
              const tuitionFile = await this.uploadFile(fileObjects[i]);
              if (tuitionFile) {
                // Store as separate fields: tuitionReceipt1, tuitionReceipt2, etc.
                student.set(`tuitionReceipt${i+1}`, tuitionFile);
                console.log(`Tuition receipt ${i+1} uploaded successfully:`, tuitionFile.url());
              }
            }
          }
          
          // Store the count of tuition receipts for easy retrieval
          student.set('tuitionReceiptCount', fileObjects.length);
        } catch (error) {
          console.error('Failed to upload tuition receipts:', error);
          throw error;
        }
      } else if (studentData.tuitionReceipt instanceof File) {
        // Handle single file for backward compatibility
        try {
          console.log('Uploading single tuition receipt...');
          const tuitionFile = await this.uploadFile(studentData.tuitionReceipt);
          if (tuitionFile) {
            student.set('tuitionReceipt', tuitionFile); // Keep for backward compatibility
            student.set('tuitionReceipt1', tuitionFile); // Also store as numbered field
            student.set('tuitionReceiptCount', 1);
            console.log('Tuition receipt uploaded successfully:', tuitionFile.url());
          }
        } catch (error) {
          console.error('Failed to upload tuition receipt:', error);
          throw error;
        }
      }

      // Initialize notes
      const initialNote = {
        content: studentData.notes || '新建学员',
        date: new Date().toISOString()
      };
      student.set('notes', [initialNote]);

      // Save and verify the complete object
      const savedStudent = await student.save();
      console.log('Student created successfully');
      
      // Add activity log
      await activityLogService.logActivity(
        '创建学员',
        `创建学员 ${studentData.name} (${studentData.uniqueId})`
      );
      
      return savedStudent;

    } catch (error) {
      console.error('Error in createStudent:', error);
      throw error;
    }
  },

  // Helper to get all receipt pics from a student object
  getReceiptPictures(student) {
    const count = student.get('receiptPicCount') || 0;
    const pics = [];
    
    // Try to get the original receiptPic for backward compatibility
    const legacyPic = student.get('receiptPic');
    if (legacyPic) {
      pics.push(legacyPic);
    }
    
    // Get all numbered receipt pics
    for (let i = 1; i <= count; i++) {
      const pic = student.get(`receiptPic${i}`);
      if (pic && !pics.includes(pic)) {
        pics.push(pic);
      }
    }
    
    return pics;
  },

  // Helper to get all tuition receipts from a student object
  getTuitionReceipts(student) {
    const count = student.get('tuitionReceiptCount') || 0;
    const receipts = [];
    
    // Try to get the original tuitionReceipt for backward compatibility
    const legacyReceipt = student.get('tuitionReceipt');
    if (legacyReceipt) {
      receipts.push(legacyReceipt);
    }
    
    // Get all numbered tuition receipts
    for (let i = 1; i <= count; i++) {
      const receipt = student.get(`tuitionReceipt${i}`);
      if (receipt && !receipts.includes(receipt)) {
        receipts.push(receipt);
      }
    }
    
    return receipts;
  },

  async getStudents(searchParams = {}) {
    try {
      const query = new AV.Query('Student');
      
      // Add search conditions
      if (searchParams.name) {
        query.contains('name', searchParams.name);
      }
      if (searchParams.uniqueId) {
        query.contains('uniqueId', searchParams.uniqueId);
      }
      if (searchParams.phone) {
        query.contains('phone', searchParams.phone);
      }
      if (searchParams.fileId) {
        query.contains('fileId', searchParams.fileId);
      }
      
      // Add date range filtering
      if (searchParams.startDate) {
        query.greaterThanOrEqualTo('signUpDate', searchParams.startDate);
      }
      if (searchParams.endDate) {
        query.lessThanOrEqualTo('signUpDate', searchParams.endDate);
      }

      // Order by signUpDate in descending order (newest first)
      query.descending('signUpDate');
      query.addDescending('createdAt');
      query.limit(1000);
      
      const students = await query.find();
      return students.map(student => {
        // Get all receipt pics and tuition receipts for this student
        const receiptPics = this.getReceiptPictures(student);
        const tuitionReceipts = this.getTuitionReceipts(student);
        
        return {
          id: student.id,
          attributes: {
            // Basic Information
            name: student.get('name'),
            uniqueId: student.get('uniqueId'),
            phone: student.get('phone'),
            fileId: student.get('fileId'),
            signUpDate: student.get('signUpDate'),
            gender: student.get('gender'),
            birthday: student.get('birthday'),
            ethnicity: student.get('ethnicity'),
            address: student.get('address'),
            
            // Status Fields
            status: student.get('status'),
            VIP: student.get('VIP'),
            verified: student.get('verified'),
            documented: student.get('documented'),
            interviewed: student.get('interviewed'),
            local: student.get('local'),
            transferred: student.get('transferred'),
            
            // Payment Information
            tuitionFee: student.get('tuitionFee'),
            paid: student.get('paid'),
            paymentMethod: student.get('paymentMethod'),
            receiptNumber: student.get('receiptNumber'),
            receiptPic: student.get('receiptPic'), // Keep for backward compatibility
            tuitionReceipt: student.get('tuitionReceipt'), // Keep for backward compatibility
            
            // New fields for multiple files
            receiptPics: receiptPics,
            tuitionReceipts: tuitionReceipts,
            receiptPicCount: student.get('receiptPicCount') || 0,
            tuitionReceiptCount: student.get('tuitionReceiptCount') || 0,
            
            // Study Progress
            subject1: student.get('subject1'),
            subject2: student.get('subject2'),
            subject3: student.get('subject3'),
            subject4: student.get('subject4'),
            subject1Completed: student.get('subject1Completed'),
            subject2Completed: student.get('subject2Completed'),
            subject3Completed: student.get('subject3Completed'),
            subject4Completed: student.get('subject4Completed'),
            
            // Exam Information 
            exam1: student.get('exam1'),
            exam2: student.get('exam2'),
            exam3: student.get('exam3'),
            exam4: student.get('exam4'),
            
            // Notes and Exams
            notes: student.get('notes') || [],
            exams: student.get('exams') || [],
            
            // Withdrawal Information
            withdrawDate: student.get('withdrawDate'),
            withdrawReason: student.get('withdrawReason'),
            withdrawReceipt: student.get('withdrawReceipt'),
            
            // Additional fields
            referrer: student.get('referrer'),
            
            // Metadata
            createdAt: student.get('createdAt'),
            updatedAt: student.get('updatedAt'),
          }
        }
      });
    } catch (error) {
      console.error('Get students error:', error);
      throw error;
    }
  },

  async updateStudent(id, data) {
    try {
      // Uniqueness checks
      if (data.uniqueId) {
        const isUniqueId = await this.checkUniqueness('uniqueId', data.uniqueId, id);
        if (!isUniqueId) {
          throw new Error('该身份证号已存在');
        }
      }

      if (data.fileId) {
        const isUniqueFileId = await this.checkUniqueness('fileId', data.fileId, id);
        if (!isUniqueFileId) {
          throw new Error('该档案号已存在');
        }
      }

      // Get current student to access existing notes
      const query = new AV.Query('Student');
      const student = await query.get(id);
      const existingNotes = student.get('notes') || [];

      // Add verification note if verified field is being updated
      if ('verified' in data) {
        const timestamp = new Date().toLocaleString('zh-CN', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: false
        });

        const verificationNote = this.createActionNote(
          data.verified 
            ? `审核通过，审核时间：${timestamp}` 
            : `审核不通过，审核时间：${timestamp}`
        );
        existingNotes.push(verificationNote);
        data.notes = existingNotes;
      }
      // Add regular modification note if other fields were changed
      else {
        const modifiedFields = Object.entries(data)
          .filter(([key, value]) => 
            student.get(key) !== value && 
            key !== 'notes' && 
            key !== 'receiptPic' &&
            key !== 'tuitionReceipt' &&
            !key.startsWith('receiptPic') &&
            !key.startsWith('tuitionReceipt') &&
            value !== undefined
          )
          .map(([key, value]) => {
            const oldValue = student.get(key);
            return `${key}: ${oldValue} → ${value}`;
          });

        if (modifiedFields.length > 0) {
          const modificationNote = this.createActionNote(
            '修改了学员信息',
            modifiedFields.join('，')
          );
          data.notes = [...existingNotes, modificationNote];
        }
      }

      // Handle file uploads for receipt pics
      if (data.receiptPic && Array.isArray(data.receiptPic) && data.receiptPic.length > 0) {
        try {
          console.log('Uploading receipt pics...');
          const fileObjects = data.receiptPic
            .filter(file => file instanceof File || file.originFileObj)
            .map(file => file instanceof File ? file : file.originFileObj);
          
          // Get current count to determine starting index
          const currentCount = student.get('receiptPicCount') || 0;
          let newCount = currentCount;
          
          // Upload each file individually and store in numbered fields
          for (let i = 0; i < fileObjects.length; i++) {
            if (fileObjects[i]) {
              const receiptFile = await this.uploadFile(fileObjects[i]);
              if (receiptFile) {
                // Store as separate fields, append to existing ones
                newCount++;
                student.set(`receiptPic${newCount}`, receiptFile);
                console.log(`Receipt pic ${newCount} uploaded successfully:`, receiptFile.url());
              }
            }
          }
          
          // Update the count
          student.set('receiptPicCount', newCount);
          
          // Delete receiptPic from data to prevent overwriting
          delete data.receiptPic;
        } catch (error) {
          console.error('Failed to upload receipt pics:', error);
          throw error;
        }
      }

      // Handle file uploads for tuition receipts
      if (data.tuitionReceipt && Array.isArray(data.tuitionReceipt) && data.tuitionReceipt.length > 0) {
        try {
          console.log('Uploading tuition receipts...');
          const fileObjects = data.tuitionReceipt
            .filter(file => file instanceof File || file.originFileObj)
            .map(file => file instanceof File ? file : file.originFileObj);
          
          // Get current count to determine starting index
          const currentCount = student.get('tuitionReceiptCount') || 0;
          let newCount = currentCount;
          
          // Upload each file individually and store in numbered fields
          for (let i = 0; i < fileObjects.length; i++) {
            if (fileObjects[i]) {
              const tuitionFile = await this.uploadFile(fileObjects[i]);
              if (tuitionFile) {
                // Store as separate fields, append to existing ones
                newCount++;
                student.set(`tuitionReceipt${newCount}`, tuitionFile);
                console.log(`Tuition receipt ${newCount} uploaded successfully:`, tuitionFile.url());
              }
            }
          }
          
          // Update the count
          student.set('tuitionReceiptCount', newCount);
          
          // Delete tuitionReceipt from data to prevent overwriting
          delete data.tuitionReceipt;
        } catch (error) {
          console.error('Failed to upload tuition receipts:', error);
          throw error;
        }
      }

      // Proceed with update
      Object.entries(data).forEach(([key, value]) => {
        if (!key.startsWith('receiptPic') && !key.startsWith('tuitionReceipt')) {
          student.set(key, value);
        }
      });
      
      const savedStudent = await student.save(null, { useMasterKey: true });
      
      // Log the update activity
      const modifiedFields = Object.keys(data)
        .filter(key => 
          student.get(key) !== data[key] && 
          key !== 'notes' && 
          key !== 'receiptPic' && 
          key !== 'tuitionReceipt' &&
          !key.startsWith('receiptPic') &&
          !key.startsWith('tuitionReceipt')
        )
        .map(key => `${key}: ${student.get(key)} → ${data[key]}`);
      
      if (modifiedFields.length > 0) {
        await activityLogService.logActivity(
          '更新学员信息',
          `更新学员 ${student.get('name')} 的信息: ${modifiedFields.join(', ')}`
        );
      }
      
      return savedStudent;
    } catch (error) {
      console.error('Update student error:', error);
      throw error;
    }
  },

  async withdrawStudent(id, withdrawData) {
    try {
      if (!withdrawData.withdrawReceipt || !withdrawData.reason) {
        throw new Error('请填写完整的退证信息');
      }

      // Upload withdraw receipt
      const withdrawFile = await this.uploadFile(withdrawData.withdrawReceipt);
      if (!withdrawFile) {
        throw new Error('退证收据上传失败');
      }

      // Get current student to access existing notes
      const query = new AV.Query('Student');
      const student = await query.get(id);
      const existingNotes = student.get('notes') || [];

      // Add withdraw request note
      const withdrawNote = this.createActionNote(
        '申请退学',
        `原因：${withdrawData.reason || '未说明'}`
      );
      
      // Update student status and notes
      student.set('status', '退证审核中');
      student.set('withdrawDate', new Date().toISOString().split('T')[0]);
      student.set('withdrawReceipt', withdrawFile);
      student.set('withdrawReason', withdrawData.reason);
      student.set('notes', [...existingNotes, withdrawNote]);

      const savedStudent = await student.save();
      
      await activityLogService.logActivity(
        '申请退学',
        `学员 ${student.get('name')} 申请退学，原因: ${withdrawData.reason}`
      );
      
      return savedStudent;
    } catch (error) {
      console.error('Withdraw student error:', error);
      throw error;
    }
  },

  async getWithdrawalStudents() {
    try {
      console.log('Starting getWithdrawalStudents query');
      const query = new AV.Query('Student');
      query.equalTo('status', '退证审核中');
      
      const students = await query.find();
      console.log('Raw withdrawal query results:', students);

      const mappedStudents = students.map(student => ({
        objectId: student.id,
        name: student.get('name'),
        uniqueId: student.get('uniqueId'),
        tuitionFee: student.get('tuitionFee'),
        withdrawReason: student.get('withdrawReason'),
        withdrawReceipt: student.get('withdrawReceipt')?.url(),
      }));

      console.log('Mapped withdrawal students:', mappedStudents);
      return mappedStudents;
    } catch (error) {
      console.error('Get withdrawal students error:', error);
      throw error;
    }
  },

  async verifyWithdrawal(id, approved) {
    try {
      const query = new AV.Query('Student');
      const student = await query.get(id);
      const existingNotes = student.get('notes') || [];

      // Add verification note
      const verificationNote = this.createActionNote(
        approved ? '通过退学申请' : '拒绝退学申请',
        approved ? '退学申请已通过' : '退学申请未通过'
      );

      // Update status based on verification result
      student.set('status', approved ? '退学' : '退证审核失败');
      student.set('notes', [...existingNotes, verificationNote]);
      student.set('withdrawVerifiedAt', new Date().toISOString());

      return await student.save();
    } catch (error) {
      console.error('Verify withdrawal error:', error);
      throw error;
    }
  },

  async getUnverifiedStudents() {
    try {
      console.log('Starting getUnverifiedStudents query');
      const query = new AV.Query('Student');
      query.equalTo('verified', false);
      query.notEqualTo('status', '退学'); // Exclude withdrawn students
      
      console.log('Query parameters:', {
        verified: false,
        excludeStatus: '退学'
      });

      const students = await query.find();
      console.log('Raw query results:', students);

      const mappedStudents = students.map(student => ({
        objectId: student.id,
        name: student.get('name'),
        uniqueId: student.get('uniqueId'),
        tuitionFee: student.get('tuitionFee'),
        receiptNumber: student.get('receiptNumber'),
        receiptPic: student.get('receiptPic')?.url(),
        tuitionReceipt: student.get('tuitionReceipt')?.url(),
      }));

      console.log('Mapped students:', mappedStudents);
      return mappedStudents;
    } catch (error) {
      console.error('Get unverified students error:', error);
      throw error;
    }
  },

  async getUndocumentedStudents() {
    try {
      const query = new AV.Query('Student');
      query.equalTo('documented', false);  // 未审档的学员
      query.equalTo('verified', true);     // 已审核的学员
      query.ascending('signUpDate');       // 按报名日期排序
      query.limit(1000);                   // 设置一个合理的限制

      console.log('Fetching undocumented students...');
      const results = await query.find();
      console.log(`Found ${results.length} undocumented students`);

      return results.map(student => ({
        objectId: student.id,
        name: student.get('name'),
        uniqueId: student.get('uniqueId'),
        signUpDate: student.get('signUpDate'),
        notes: student.get('notes') || [],
        // 添加其他需要的字段
        ...student.toJSON()
      }));
    } catch (error) {
      console.error('Error fetching undocumented students:', error);
      throw error;
    }
  },
}; 